import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { InviteShareV2DialogComponent } from '@dialogs/invite-share-dialog/invite-share-dialog.component';
import { MdsIcon } from '@mds/icons';
import {
  ButtonComponent,
  ConfirmationDialogComponent,
  ContextMenuOption,
  ContextMenuOptionTypeEnum,
  DialogModule,
  DropdownMenuComponent,
  SearchBarComponent,
  TemplateCardComponent
} from '@mds/ui';
import { PIVOT_MANAGER, REPORT_HISTORY } from '@services/apps-data';
import { GlobalDataService } from '@services/global-data.service';
import { ReportApiService } from '@services/report-api.service';
import { MenuService } from '@services/menu.service';
import { ToastrService } from '@services/toaster.service';
import { addTagAndIconToReports, getReportPath, Report } from '@utils/reports';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

enum SortDirectionEnum {
  ASCENDING = 0,
  DESCENDING = 1
}

enum SortGroupEnum {
  ALL = 'all',
  SAVED = 'saved'
}

enum MenuOptionIdEnum {
  DELETE = 'delete',
  PIN = 'pin',
  RENAME = 'rename',
  SHARE = 'share'
}

@Component({
  selector: 'app-report-landing',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    DialogModule,
    DropdownMenuComponent,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SearchBarComponent,
    TemplateCardComponent
  ],
  templateUrl: './report-landing.component.html',
  styleUrls: ['./report-landing.component.scss']
})
export class ReportLandingComponent implements OnInit {
  @ViewChild('deleteReportDialogActions')
  deleteReportDialogActions!: TemplateRef<any>;
  @ViewChild('renameReportDialogBody')
  renameReportDialogBody!: TemplateRef<any>;
  @ViewChild('renameReportDialogActions')
  renameReportDialogActions!: TemplateRef<any>;
  @ViewChild('shareReportDialogActions')
  shareReportDialogActions!: TemplateRef<any>;
  destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private reportApiService = inject(ReportApiService);
  allSavedReports: Report[] = [];
  searchText = '';
  sortingDirectionOptions = [
    {
      text: 'Name (A > Z)',
      value: SortDirectionEnum.ASCENDING
    },
    {
      text: 'Name (Z > A)',
      value: SortDirectionEnum.DESCENDING
    }
  ];
  filterCategoryOptions = [
    {
      text: 'All',
      value: SortDirectionEnum.ASCENDING
    }
  ];
  sortDirectionAll = new FormControl([this.sortingDirectionOptions[0]]);
  sortDirectionSaved = new FormControl([this.sortingDirectionOptions[0]]);
  filterCategory = new FormControl([this.filterCategoryOptions[0]]);
  newReportName = '';
  deleteReportDialogRef: MatDialogRef<any>;
  renameReportDialogRef: MatDialogRef<any>;
  shareReportDialogRef: MatDialogRef<any>;
  private menuService = inject(MenuService);
  private getAllReportTemplates$: Observable<any> = this.reportApiService.searchAvailableReports().pipe(
    map((res: any[]) => res.map((report) => this.removeReportPrefix(report))),
    tap((res) => {
      this.filterCategoryOptions.push(
        ...[...new Set(res.map((item) => item.report_category))].map((category: string) => ({
          text: category,
          value: SortDirectionEnum.ASCENDING
        }))
      );
    })
  );
  private getSavedReportTemplates$: Observable<any> = this.reportApiService
    .getAllSavedReportTemplates()
    .pipe(map((res: any[]) => res.map((report) => this.removeReportPrefix(report))));
  private _reportTemplates$ = new BehaviorSubject<{
    allReports: { letter: string; reports: any[] }[];
    savedReports: Report[];
  }>({ allReports: null, savedReports: [] });
  reportTemplates$: Observable<{
    allReports: { letter: string; reports: any[] }[];
    savedReports: Report[];
  }> = this._reportTemplates$.asObservable();
  private allReports: Report[] = [];
  private fetchReports$: Observable<any> = forkJoin({
    allReports: this.getAllReportTemplates$,
    savedReports: this.getSavedReportTemplates$
  }).pipe(
    tap((res) => {
      this.allReports = this.sortReportsByTitle(res.allReports, SortDirectionEnum.ASCENDING);
      this.allSavedReports = this.sortReportsByTitle(res.savedReports, SortDirectionEnum.ASCENDING);
    })
  );

  constructor(
    private dialog: MatDialog,
    private globalDataService: GlobalDataService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.initGlobalData();
    this.fetchReports();
    this.sortDirectionAll.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        this.sortReportsByTitle(this.allReports, value[0].value);
        this.filterAndSetReports();
      });

    this.sortDirectionSaved.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        this.sortReportsByTitle(this.allSavedReports, value[0].value);
        this.filterAndSetReports();
      });

    this.filterCategory.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(() => {
      this.filterAndSetReports();
    });

    this.menuService.reload$
      .pipe(
        filter((reload) => !!reload),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.fetchReports();
      });
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      this.parseAllMenuEvent(action?.key);
    });

    this.menuService.getApps().subscribe((res) => {
      const marioApp = res.find((app) => app.name === 'mario');

      const centerActions = [
        {
          label: 'History',
          key: 'history',
          icon: MdsIcon.HISTORY,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: true
        }
      ];

      if (marioApp) {
        centerActions.push({
          label: marioApp.app_name,
          key: marioApp.name,
          icon: MdsIcon[marioApp.icon_app],
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: true
        });
      }

      this.menuService.setMenuConfig({
        activeApp: {
          mainIcon: {
            icon: MdsIcon.APP_PIVOT_MANAGER,
            pathColors: ['#FFF']
          },
          rid: '',
          name: 'Report Explorer',
          icons: [],
          subtitle: '',
          menuTrigger: 'selectApps'
        },
        centerActions
      });
      this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
        if (action?.key === 'history') {
          this.router.navigate(['/reports/history']);
        }
        if (marioApp && action?.key === marioApp?.name) {
          this.router.navigate([marioApp.target]);
        }
      });
    });
  }

  parseAllMenuEvent(key: any) {
    const actionHandlers: Record<string, () => void> = {
      history: () => this.goToReportHistory()
    };
    actionHandlers[key]?.();
  }

  goToReportHistory(): void {
    this.router.navigate(['/reports/history']);
  }

  onSearchTextChange(value: string): void {
    this.searchText = value;
    this.filterAndSetReports();
  }

  onMenuOptionClicked(item: ContextMenuOption, report: Report): void {
    switch (item.id) {
      case MenuOptionIdEnum.DELETE:
        this.openDeleteReportDialog(report);
        break;
      case MenuOptionIdEnum.PIN:
        this.pinReportToDashboard(report);
        break;
      case MenuOptionIdEnum.RENAME:
        this.openRenameReportDialog(report);
        break;
      case MenuOptionIdEnum.SHARE:
        this.openShareReportDialog(report);
        break;
      default:
        break;
    }
  }

  deleteReport(): void {
    const rid: string = this.deleteReportDialogRef.componentInstance.dialogData.report.original_rid.replace('#', '');
    this.reportApiService
      .deleteReportTemplate(rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.deleteReportDialogRef.close();
        this.toastrService.showMessage('The template has been successfully deleted.');
        this.fetchReports();
      });
  }

  renameReport(): void {
    const payload = {
      name: this.newReportName
    };
    const rid: string = this.renameReportDialogRef.componentInstance.dialogData.report.original_rid.replace('#', '');
    this.reportApiService
      .renameReportTemplate(payload, rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.renameReportDialogRef.close();
        this.toastrService.showMessage('The template has been successfully renamed');
        this.fetchReports();
      });
  }

  getReportLink(isTemplate: boolean, reportData: Report) {
    const rid = isTemplate ? reportData.original_rid.replace('#', '') : reportData.rid.replace('#', '');
    return getReportPath(isTemplate, rid, isTemplate ? { id: reportData.original_rid.replace('#', '') } : null);
  }

  private initGlobalData(): void {
    this.globalDataService.setUrlParam({});
    this.globalDataService.setAllActions([]);
    this.globalDataService.setCurrentActiveApp({ name: 'app.reportsList' });
    this.globalDataService.setMegaMenuList([PIVOT_MANAGER, REPORT_HISTORY]);
    this.globalDataService.setActiveApp(PIVOT_MANAGER);
  }

  private fetchReports(): void {
    this.fetchReports$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortReportsByTitle(this.allSavedReports, this.sortDirectionAll.value[0].value);
      this.sortReportsByTitle(this.allReports, this.sortDirectionSaved.value[0].value);
      this.filterAndSetReports();
    });
  }

  private setReports(allReports: Report[], savedReports: Report[]): void {
    this._reportTemplates$.next({
      allReports: this.groupReportsByLetter(addTagAndIconToReports(allReports)),
      savedReports: addTagAndIconToReports(this.addMenuOptionsToReports(savedReports))
    });
  }

  private filterReportsByString(reports: Report[], text: string): Report[] {
    if (!text) {
      return reports;
    }

    return reports.filter((report) => {
      const titleMatch = report.title.toUpperCase().indexOf(text.toUpperCase()) !== -1;
      const categoryMatch = report.report_category.toUpperCase().indexOf(text.toUpperCase()) !== -1;

      return titleMatch || categoryMatch;
    });
  }

  private filterReportsByCategory(reports: Report[], category: string): Report[] {
    if (!category || category === 'All') {
      return reports;
    }

    return reports.filter((report) => report.report_category === category);
  }

  private removeReportPrefix(report) {
    return {
      ...report,
      description: report.description.replace('Report Template | ', '')
    };
  }

  private addMenuOptionsToReports(reports: Report[]) {
    return reports.map((report) => ({
      ...report,
      menuOptions: [
        {
          label: 'Rename',
          id: MenuOptionIdEnum.RENAME
        },
        {
          label: 'Share',
          id: MenuOptionIdEnum.SHARE
        },
        {
          label: report.is_dashboard ? 'Unpin from Dashboard' : 'Pin to Dashboard',
          id: MenuOptionIdEnum.PIN
        },
        {
          label: 'Delete',
          id: MenuOptionIdEnum.DELETE,
          type: ContextMenuOptionTypeEnum.DESTRUCTIVE
        }
      ]
    }));
  }

  private groupReportsByLetter(allReports: Report[]) {
    if (!allReports.length) {
      return null;
    }

    const groupedReports: { [key: string]: Report[] } = {};

    allReports.forEach((report) => {
      const firstLetter = report.title.charAt(0).toUpperCase();
      if (!groupedReports[firstLetter]) {
        groupedReports[firstLetter] = [];
      }
      groupedReports[firstLetter].push(report);
    });

    return Object.entries(groupedReports).map(([letter, reports]) => ({
      letter,
      reports
    }));
  }

  private sortReportsByTitle(reports: Report[], sortDirection: SortDirectionEnum): Report[] {
    return reports.sort((a, b) => (sortDirection ? b.title.localeCompare(a.title) : a.title.localeCompare(b.title)));
  }

  private filterAndSetReports(): void {
    const filteredAllReports = this.filterReportsByCategory(
      this.filterReportsByString(this.allReports, this.searchText),
      this.filterCategory.value[0].text
    );
    const filteredSavedReports = this.filterReportsByCategory(
      this.filterReportsByString(this.allSavedReports, this.searchText),
      this.filterCategory.value[0].text
    );

    this.setReports(filteredAllReports, filteredSavedReports);
  }

  private openShareReportDialog(report: Report): void {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));

    this.shareReportDialogRef = this.dialog.open(InviteShareV2DialogComponent, {
      data: {
        type: 'showInvite',
        value: 'Share',
        data: {
          title: 'Share',
          actions: this.shareReportDialogActions,
          fr: userInfo.email,
          usersLackingAccessList: '',
          isfrom: 'report-template',
          rid: report.original_rid.replace('#', '')
        }
      },
      panelClass: 'show-invite'
    });
  }

  private openDeleteReportDialog(report: Report): void {
    this.deleteReportDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Permanently delete report template?',
        content:
          'You will delete this template only for yourself. Once deleted, you won’t be able to recover the template anymore. Reports generated with this template won’t be affected.',
        actions: this.deleteReportDialogActions,
        report
      }
    });
  }

  private openRenameReportDialog(report: Report): void {
    this.renameReportDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Rename report template',
        content: 'The template will be renamed for all users it has been shared with.',
        body: this.renameReportDialogBody,
        actions: this.renameReportDialogActions,
        report
      }
    });

    this.renameReportDialogRef.afterClosed().subscribe(() => {
      this.newReportName = '';
    });
  }

  private pinReportToDashboard(report: Report): void {
    const rid = report.original_rid.replace('#', '');
    const payload = {
      is_dashboard: !report.is_dashboard,
      rid
    };
    this.reportApiService
      .saveReportGrid(rid, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const pinnedText = report.is_dashboard ? 'unpinned from' : 'pinned to';
        this.toastrService.showMessage(`The template has been successfully ${pinnedText} the Dashboard`);
        this.fetchReports();
      });
  }
}
